import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { wibToday, fmtWib } from "@/lib/bizdate";
import { downloadPdf } from "./PurchaseDetail";
import { useAuth } from "@/context/AuthContext";
import { useMasters } from "@/hooks/useMasters";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { numberID, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Factory, X } from "lucide-react";

const STAGES = [
  { key: "cutting", label: "Cutting", perm: "production.cutting", fields: [["qty", "Qty"]] },
  { key: "sewing", label: "Sewing", perm: "production.sewing", fields: [["qty", "Qty"]] },
  { key: "finishing", label: "Finishing", perm: "production.finishing", fields: [["qty", "Qty"]] },
  { key: "qc", label: "QC", perm: "production.qc", fields: [["passed", "Passed"], ["rejected", "Reject"]] },
  { key: "disposition", label: "Keputusan Reject", perm: "production.qc", fields: [["rework", "Rework"], ["scrap", "Scrap"]] },
  { key: "rework-complete", label: "Rework Selesai", perm: "production.qc", fields: [["qty", "Qty"]] },
];

export default function ProductionOrders() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const canManage = can("production.order.manage");
  const load = () => { setRows(null); api.get("/production/production-orders").then((r) => setRows(r.data)); };
  useEffect(load, []);

  const progress = (po) => {
    const t = po.target_qty || 0;
    const done = po.items.reduce((s, i) => s + (i.staged_qty || 0), 0);
    return t ? Math.round((done / t) * 100) : 0;
  };

  return (
    <div>
      <PageHeader title="Production Order" subtitle="Perintah produksi konveksi (size breakdown 1 keluarga produk). Barang jadi hanya dari QC Passed." testId="prod-header">
        {canManage && <Button onClick={() => setOpen(true)} data-testid="add-prod-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Production Order</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada Production Order" icon={Factory} /> : (
          <Table>
            <TableHeader><TableRow><TableHead>No PO Produksi</TableHead><TableHead>Tanggal</TableHead><TableHead>Produk</TableHead><TableHead className="text-right">Target</TableHead><TableHead>Progress (QC Passed)</TableHead><TableHead>Dibuat Oleh</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((po) => (
                <TableRow key={po.id} data-testid={`prod-row-${po.po_prod_no}`}>
                  <TableCell className="font-mono text-xs">{po.po_prod_no}
                    {po.recreated_from_po_prod_no && (
                      <button type="button" onClick={() => { const src = rows.find((r) => r.id === po.recreated_from_production_order_id); if (src) setDetail(src.id); else toast.info("PO sumber tidak tersedia"); }} data-testid={`prod-recreated-badge-${po.po_prod_no}`} title={`${po.is_replacement ? "Pengganti" : "Dibuat ulang"} dari ${po.recreated_from_po_prod_no}`} className="mt-1 block text-[10px] font-normal text-indigo-600 hover:underline">↻ {po.is_replacement ? "Pengganti" : "Dibuat ulang"} dari {po.recreated_from_po_prod_no}</button>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDate(po.tanggal)}</TableCell>
                  <TableCell className="text-sm">{po.fabric_name} · <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full border border-slate-300" style={{ background: po.color_hex }} />{po.color_name}</span> <span className="text-xs text-slate-400">· {po.items.length} varian</span></TableCell>
                  <TableCell className="text-right">{numberID(po.target_qty)}</TableCell>
                  <TableCell><div className="flex items-center gap-2"><div className="h-1.5 w-24 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-indigo-500" style={{ width: `${progress(po)}%` }} /></div><span className="text-xs text-slate-500">{progress(po)}%</span></div></TableCell>
                  <TableCell className="text-xs text-slate-600" data-testid={`prod-creator-${po.po_prod_no}`}>{po.created_by_name || <span className="text-slate-400">Tidak tersedia</span>}</TableCell>
                  <TableCell><StatusBadge status={po.status} /></TableCell>
                  <TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => setDetail(po.id)} data-testid={`prod-detail-${po.po_prod_no}`}>Detail</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {open && <CreateDialog onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {detail && <DetailDialog id={detail} onClose={() => setDetail(null)} onChanged={load} onOpenPo={setDetail} />}
    </div>
  );
}

function CreateDialog({ onClose, onSaved, editPo }) {
  const today = wibToday();
  const { masters } = useMasters();
  const [wh, setWh] = useState([]);
  const [f, setF] = useState(editPo ? {
    tanggal: editPo.tanggal || today, fabric_type_id: editPo.fabric_type_id || "", color_id: editPo.color_id || "",
    warehouse_id: editPo.warehouse_id || "", target_completion_date: editPo.target_completion_date || "",
    priority: editPo.priority || "Normal", notes: editPo.notes || "",
    planned_fabric_roll_qty: editPo.planned_fabric_roll_qty ?? "",
  } : { tanggal: today, fabric_type_id: "", color_id: "", warehouse_id: "", target_completion_date: "", priority: "Normal", notes: "", planned_fabric_roll_qty: "" });
  const [rows, setRows] = useState(editPo ? (editPo.items || []).map((it) => ({ age_category_id: it.age_category_id, sleeve_type_id: it.sleeve_type_id, size_id: it.size_id, target_qty: it.target_qty })) : [{ age_category_id: "", sleeve_type_id: "", size_id: "", target_qty: 1 }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const konv = masters.warehouses.filter((w) => w.type === "KONVEKSI");
    setWh(konv);
    if (!editPo && konv.length === 1) setF((p) => ({ ...p, warehouse_id: konv[0].id }));
  }, [masters.warehouses]);

  // Edit mode: re-assert preselected ids once masters finish loading so Selects show their labels.
  useEffect(() => {
    if (editPo && (masters.fabric_types?.length || masters.colors?.length)) {
      setF((p) => ({ ...p,
        fabric_type_id: p.fabric_type_id || editPo.fabric_type_id || "",
        color_id: p.color_id || editPo.color_id || "",
        warehouse_id: p.warehouse_id || editPo.warehouse_id || "" }));
    }
  }, [masters.fabric_types, masters.colors, masters.warehouses]);

  const sizesFor = (ageId) => {
    const code = masters.age_categories.find((a) => a.id === ageId)?.code;
    return masters.sizes.filter((s) => s.size_category_code === code);
  };
  const setRow = (i, patch) => setRows(rows.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const familyReady = f.fabric_type_id && f.color_id;
  const selFabric = masters.fabric_types?.find((x) => x.id === f.fabric_type_id);
  const fabricIsCW = selFabric?.tracking_mode === "CATCH_WEIGHT_ROLL";

  const save = async () => {
    if (!familyReady) return toast.error("Pilih bahan & warna");
    if (!f.warehouse_id) return toast.error("Pilih Inventory Konveksi");
    if (rows.length === 0 || rows.some((r) => !r.age_category_id || !r.sleeve_type_id || !r.size_id || Number(r.target_qty) <= 0))
      return toast.error("Lengkapi umur, lengan, ukuran & target > 0 tiap item");
    if (fabricIsCW) {
      const n = Number(f.planned_fabric_roll_qty);
      if (f.planned_fabric_roll_qty === "" || f.planned_fabric_roll_qty == null || !Number.isInteger(n) || n < 1)
        return toast.error("Kebutuhan Kain wajib diisi untuk bahan Catch Weight.");
    }
    setSaving(true);
    const payload = {
      ...f, target_completion_date: f.target_completion_date || null,
      planned_fabric_roll_qty: f.planned_fabric_roll_qty === "" || f.planned_fabric_roll_qty == null ? null : Number(f.planned_fabric_roll_qty),
      items: rows.map((r) => ({ age_category_id: r.age_category_id, sleeve_type_id: r.sleeve_type_id, size_id: r.size_id, target_qty: Number(r.target_qty) })),
    };
    const submit = async (confirmLarge) => {
      try {
        if (editPo) {
          await api.patch(`/production/production-orders/${editPo.id}`, { ...payload, confirm_large: confirmLarge });
          toast.success(editPo.status === "Disetujui" ? "PO diperbarui — perlu persetujuan ulang" : "Production Order diperbarui");
        } else {
          await api.post("/production/production-orders", { ...payload, confirm_large: confirmLarge });
          toast.success("Production Order dibuat (Draft)");
        }
        onSaved();
      } catch (e) {
        const d = e?.response?.data?.detail || "";
        if (typeof d === "string" && d.startsWith("KONFIRMASI_QTY::")) {
          if (window.confirm(d.replace("KONFIRMASI_QTY::", ""))) return submit(true);
        } else { toast.error(apiError(e)); }
      }
    };
    await submit(false);
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{editPo ? `Edit ${editPo.po_prod_no}` : "Production Order Baru"}</DialogTitle></DialogHeader>
        {editPo && editPo.status === "Disetujui" && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="prod-edit-reapproval-note">Mengubah PO yang sudah Disetujui akan mengembalikannya ke status Menunggu Persetujuan (perlu persetujuan ulang).</p>}
        <p className="text-xs text-slate-400">Satu order = satu Bahan + satu Warna. Umur, lengan, dan ukuran boleh berbeda antar item.</p>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs text-slate-500">Tanggal</Label><Input type="date" className="mt-1" value={f.tanggal} onChange={(e) => setF({ ...f, tanggal: e.target.value })} data-testid="prod-tanggal" /></div>
          <div><Label className="text-xs text-slate-500">Bahan</Label><Select value={f.fabric_type_id} onValueChange={(v) => setF({ ...f, fabric_type_id: v })}><SelectTrigger className="mt-1" data-testid="prod-fabric"><SelectValue placeholder="Pilih..." /></SelectTrigger><SelectContent>{masters.fabric_types.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs text-slate-500">Warna</Label><Select value={f.color_id} onValueChange={(v) => setF({ ...f, color_id: v })}><SelectTrigger className="mt-1" data-testid="prod-color"><SelectValue placeholder="Pilih..." /></SelectTrigger><SelectContent>{masters.colors.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs text-slate-500">Inventory Konveksi</Label><Select value={f.warehouse_id} onValueChange={(v) => setF({ ...f, warehouse_id: v })}><SelectTrigger className="mt-1" data-testid="prod-warehouse"><SelectValue placeholder="Pilih..." /></SelectTrigger><SelectContent>{wh.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs text-slate-500">Target Selesai</Label><Input type="date" className="mt-1" value={f.target_completion_date} onChange={(e) => setF({ ...f, target_completion_date: e.target.value })} data-testid="prod-target-date" /></div>
          <div><Label className="text-xs text-slate-500">Prioritas</Label><Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}><SelectTrigger className="mt-1" data-testid="prod-priority"><SelectValue /></SelectTrigger><SelectContent>{["Rendah", "Normal", "Tinggi", "Urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
          {fabricIsCW && <div><Label className="text-xs text-slate-500">Kebutuhan Kain (Roll)*</Label><Input type="number" min="1" step="1" className="mt-1" placeholder="Masukkan jumlah roll" value={f.planned_fabric_roll_qty} onChange={(e) => setF({ ...f, planned_fabric_roll_qty: e.target.value })} data-testid="prod-planned-roll" /><p className="mt-0.5 text-[10px] text-slate-400">Wajib untuk bahan Catch Weight. Dapat ditambah kemudian melalui Tambah Kebutuhan Kain dengan alasan dan audit trail.</p></div>}
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between"><Label className="text-sm font-semibold">Item Produksi (Umur · Lengan · Ukuran · Qty)</Label><Button size="sm" variant="outline" disabled={!familyReady} onClick={() => setRows([...rows, { age_category_id: "", sleeve_type_id: "", size_id: "", target_qty: 1 }])}><Plus className="mr-1 h-3.5 w-3.5" /> Item</Button></div>
          {!familyReady ? <p className="text-xs text-slate-400">Pilih bahan & warna dahulu.</p> : rows.map((r, i) => (
            <div key={i} className="mb-2 grid grid-cols-12 items-center gap-2">
              <div className="col-span-3"><Select value={r.age_category_id} onValueChange={(v) => setRow(i, { age_category_id: v, size_id: "" })}><SelectTrigger data-testid={`prod-age-${i}`}><SelectValue placeholder="Umur" /></SelectTrigger><SelectContent>{masters.age_categories.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="col-span-3"><Select value={r.sleeve_type_id} onValueChange={(v) => setRow(i, { sleeve_type_id: v })}><SelectTrigger data-testid={`prod-sleeve-${i}`}><SelectValue placeholder="Lengan" /></SelectTrigger><SelectContent>{masters.sleeve_types.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="col-span-3"><Select value={r.size_id} onValueChange={(v) => setRow(i, { size_id: v })} disabled={!r.age_category_id}><SelectTrigger data-testid={`prod-size-${i}`}><SelectValue placeholder="Ukuran" /></SelectTrigger><SelectContent>{sizesFor(r.age_category_id).map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select></div>
              <Input type="number" min="1" className="col-span-2" placeholder="Qty" value={r.target_qty} onChange={(e) => setRow(i, { target_qty: e.target.value })} data-testid={`prod-target-${i}`} />
              <Button variant="ghost" size="sm" className="col-span-1 text-rose-500" onClick={() => setRows(rows.filter((_, idx) => idx !== i))}><X className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
        <div><Label className="text-xs text-slate-500">Catatan</Label><Textarea className="mt-1" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-prod-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ po, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const doCancel = async (confirmDownstream) => {
    if (!reason.trim()) return toast.error("Alasan pembatalan wajib diisi");
    setBusy(true);
    try {
      await api.post(`/production/production-orders/${po.id}/cancel`, { reason: reason.trim(), confirm_downstream: confirmDownstream });
      toast.success("Production Order dibatalkan"); onDone();
    } catch (e) {
      const d = e?.response?.data?.detail || "";
      if (typeof d === "string" && d.startsWith("KONFIRMASI_DOWNSTREAM::")) {
        if (window.confirm(d.replace("KONFIRMASI_DOWNSTREAM::", ""))) return doCancel(true);
      } else { toast.error(apiError(e)); }
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="prod-cancel-dialog">
        <DialogHeader><DialogTitle className="font-display">Batalkan {po.po_prod_no}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Pembatalan bersifat final &amp; auditable. Alasan wajib diisi. Jika PO sudah punya aktivitas downstream, sistem akan meminta konfirmasi tambahan.</p>
        <Textarea rows={3} placeholder="Alasan pembatalan..." value={reason} onChange={(e) => setReason(e.target.value)} data-testid="prod-cancel-reason" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Tutup</Button><Button disabled={busy} className="bg-rose-600 hover:bg-rose-500" onClick={() => doCancel(false)} data-testid="prod-cancel-confirm">Batalkan PO</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReplacementDialog({ po, recon, onClose, onDone }) {
  const [qty, setQty] = useState({});
  const [sourceMode, setSourceMode] = useState("NEW_MATERIAL");
  const [cfNote, setCfNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (recon?.items) setQty(Object.fromEntries(recon.items.map((i) => [i.item_id, i.suggested_remaining])));
  }, [recon]);
  if (!recon) return null;
  const isCarry = sourceMode === "CARRY_FORWARD";
  const submit = async () => {
    const items = Object.entries(qty).map(([item_id, q]) => ({ item_id, qty: Number(q || 0) })).filter((x) => x.qty > 0);
    if (items.length === 0) return toast.error("Masukkan qty pengganti (> 0) untuk minimal satu varian");
    setBusy(true);
    try { const r = await api.post(`/production/production-orders/${po.id}/replacement`, { items, material_source_mode: sourceMode, carry_forward_note: isCarry ? (cfNote.trim() || null) : null }); toast.success(`PO Pengganti dibuat: ${r.data.po_prod_no} (Draft)`); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="prod-replacement-dialog">
        <DialogHeader><DialogTitle className="font-display">Buat PO Pengganti — dari {po.po_prod_no}</DialogTitle></DialogHeader>
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="prod-replacement-warning">PO ini sudah memiliki aktivitas produksi. Target penuh tidak boleh dibuat ulang karena dapat menyebabkan produksi ganda. Konfirmasi qty pengganti per varian (saran = Target − QC Passed).</p>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 space-y-2">
          <Label className="text-xs font-semibold text-slate-600">Sumber Material</Label>
          <Select value={sourceMode} onValueChange={setSourceMode}>
            <SelectTrigger className="bg-white" data-testid="prod-repl-source-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="NEW_MATERIAL" data-testid="prod-repl-source-new">Material Baru</SelectItem>
              <SelectItem value="CARRY_FORWARD" data-testid="prod-repl-source-carry">Lanjutkan Material/WIP dari {po.po_prod_no}</SelectItem>
            </SelectContent>
          </Select>
          {isCarry && <p className="rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-700" data-testid="prod-repl-carry-note">Material sebelumnya sudah dikeluarkan/dipotong. Kebutuhan roll baru tidak wajib. Tambahkan kebutuhan kain hanya jika terjadi kekurangan material.</p>}
          {isCarry && <Textarea rows={2} className="bg-white" placeholder="Catatan carry-forward (opsional, untuk audit)..." value={cfNote} onChange={(e) => setCfNote(e.target.value)} data-testid="prod-repl-carry-note-input" />}
        </div>
        <div className="rounded-xl border border-slate-200 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>SKU / Varian</TableHead><TableHead className="text-right">Target</TableHead><TableHead className="text-right">Cut</TableHead><TableHead className="text-right">Sewn</TableHead><TableHead className="text-right">Finish</TableHead><TableHead className="text-right">QC Passed</TableHead><TableHead className="text-right">Reject</TableHead><TableHead className="text-right">Rework</TableHead><TableHead className="text-right">Scrap</TableHead><TableHead className="text-right">Saran Sisa</TableHead><TableHead className="text-right">Qty Pengganti</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {recon.items.map((it) => (
                <TableRow key={it.item_id} data-testid={`prod-repl-row-${it.sku}`}>
                  <TableCell><div className="font-mono text-xs">{it.sku}</div><div className="text-xs text-slate-400">{it.label}</div></TableCell>
                  <TableCell className="text-right">{numberID(it.target_qty)}</TableCell>
                  <TableCell className="text-right">{numberID(it.cut_qty)}</TableCell>
                  <TableCell className="text-right">{numberID(it.sewn_qty)}</TableCell>
                  <TableCell className="text-right">{numberID(it.finished_qty)}</TableCell>
                  <TableCell className="text-right text-emerald-600">{numberID(it.qc_passed_qty)}</TableCell>
                  <TableCell className="text-right text-rose-600">{numberID(it.qc_failed_pending)}</TableCell>
                  <TableCell className="text-right">{numberID(it.rework_qty)}</TableCell>
                  <TableCell className="text-right text-slate-400">{numberID(it.scrap_qty)}</TableCell>
                  <TableCell className="text-right font-medium text-indigo-600">{numberID(it.suggested_remaining)}</TableCell>
                  <TableCell className="text-right"><Input type="number" className="w-20 text-right" value={qty[it.item_id] ?? ""} onChange={(e) => setQty((p) => ({ ...p, [it.item_id]: e.target.value }))} data-testid={`prod-repl-qty-${it.sku}`} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button disabled={busy} className="bg-amber-600 hover:bg-amber-500" onClick={submit} data-testid="prod-replacement-submit">Buat PO Pengganti</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FabricPlanDialog({ po, onClose, onDone }) {
  const planned = po.fabric_plan?.planned_fabric_roll_qty;
  const isAmend = planned != null;
  const [qty, setQty] = useState(planned != null ? String(planned) : "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const n = Number(qty);
    if (!n || n < 1) return toast.error("Kebutuhan kain minimal 1 roll");
    if (isAmend && n > planned && !reason.trim()) return toast.error("Alasan tambah kebutuhan kain wajib diisi");
    setBusy(true);
    try {
      await api.post(`/production/production-orders/${po.id}/fabric-plan`, { planned_fabric_roll_qty: n, reason: reason.trim() || null });
      toast.success("Kebutuhan kain diperbarui"); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="prod-fabric-plan-dialog">
        <DialogHeader><DialogTitle className="font-display">{isAmend ? "Tambah Kebutuhan Kain" : "Tetapkan Kebutuhan Kain"} — {po.po_prod_no}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Jumlah roll kain yang direncanakan untuk PO ini. Backend akan membatasi Issue Roll agar tidak melebihi rencana. {isAmend && "Menambah kebutuhan memerlukan alasan & tercatat pada audit."}</p>
        <div><Label className="text-xs text-slate-500">Kebutuhan Kain (Roll)</Label><Input type="number" min="1" className="mt-1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="prod-fabric-plan-qty" /></div>
        {isAmend && <div><Label className="text-xs text-slate-500">Alasan (wajib bila menambah)</Label><Textarea rows={2} className="mt-1" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="prod-fabric-plan-reason" /></div>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button disabled={busy} className="bg-indigo-600 hover:bg-indigo-500" onClick={submit} data-testid="prod-fabric-plan-submit">{busy ? "..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CorrectionDialog({ po, onClose, onDone }) {
  const FIELDS = [
    ["target_qty", "Target"], ["cut_qty", "Cutting"], ["sewn_qty", "Sewing"],
    ["finished_qty", "Finishing"], ["qc_queue", "Antrian QC"],
    ["qc_failed_pending", "Reject Menunggu"], ["rework_qty", "Rework"], ["scrap_qty", "Scrap"],
  ];
  const [itemId, setItemId] = useState(po.items[0]?.id || "");
  const [vals, setVals] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const item = po.items.find((i) => i.id === itemId);

  useEffect(() => {
    if (item) setVals(Object.fromEntries(FIELDS.map(([f]) => [f, String(item[f] ?? 0)])));
  }, [itemId]);

  const changes = item ? FIELDS
    .map(([f]) => ({ field: f, new_value: Number(vals[f]) }))
    .filter((c) => Number.isInteger(c.new_value) && c.new_value !== (item[c.field] ?? 0)) : [];

  const submit = async () => {
    if (!item) return toast.error("Pilih SKU/ukuran");
    if (!reason.trim()) return toast.error("Alasan koreksi wajib diisi");
    for (const [f, label] of FIELDS) {
      const n = Number(vals[f]);
      if (!Number.isInteger(n) || n < 0) return toast.error(`Nilai ${label} harus bilangan bulat ≥ 0`);
    }
    if (changes.length === 0) return toast.error("Tidak ada perubahan nilai");
    const summary = changes.map((c) => {
      const label = FIELDS.find(([f]) => f === c.field)[1];
      const old = item[c.field] ?? 0; const d = c.new_value - old;
      return `${label}: ${numberID(old)} → ${numberID(c.new_value)} (${d > 0 ? "+" : ""}${numberID(d)})`;
    }).join("\n");
    if (!window.confirm(`Konfirmasi Koreksi ${item.sku}\n\n${summary}\n\nAlasan: ${reason.trim()}\n\nSimpan koreksi ini?`)) return;
    setBusy(true);
    try {
      await api.post(`/production/production-orders/${po.id}/correct`, { item_id: itemId, changes, reason: reason.trim() });
      toast.success("Koreksi produksi tersimpan");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prod-correction-dialog">
        <DialogHeader><DialogTitle className="font-display">Koreksi Produksi — {po.po_prod_no}</DialogTitle></DialogHeader>
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">Koreksi khusus Owner untuk memperbaiki kesalahan input operator. QC Passed &amp; Barang Jadi tidak dapat dikoreksi pada rilis ini. Seluruh koreksi tercatat pada audit trail.</p>
        <div>
          <Label className="text-xs text-slate-500">SKU / Ukuran</Label>
          <Select value={itemId} onValueChange={setItemId}>
            <SelectTrigger className="mt-1" data-testid="prod-correction-item"><SelectValue placeholder="Pilih SKU/ukuran" /></SelectTrigger>
            <SelectContent>{po.items.map((it) => <SelectItem key={it.id} value={it.id}>{it.sku} · {it.age_name} {it.sleeve_name} {it.size_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {item && (
          <div className="rounded-xl border border-slate-200 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Field</TableHead><TableHead className="text-right">Nilai Lama</TableHead><TableHead className="text-right">Nilai Baru</TableHead><TableHead className="text-right">Selisih</TableHead></TableRow></TableHeader>
              <TableBody>
                {FIELDS.map(([f, label]) => {
                  const old = item[f] ?? 0; const nv = Number(vals[f]);
                  const d = Number.isInteger(nv) ? nv - old : 0;
                  return (
                    <TableRow key={f} data-testid={`prod-correction-row-${f}`}>
                      <TableCell className="text-sm font-medium">{label}</TableCell>
                      <TableCell className="text-right text-slate-500">{numberID(old)}</TableCell>
                      <TableCell className="text-right"><Input type="number" min="0" step="1" className="w-24 text-right ml-auto" value={vals[f] ?? ""} onChange={(e) => setVals((p) => ({ ...p, [f]: e.target.value }))} data-testid={`prod-correction-input-${f}`} /></TableCell>
                      <TableCell className={`text-right text-xs font-medium ${d > 0 ? "text-emerald-600" : d < 0 ? "text-rose-600" : "text-slate-400"}`}>{d > 0 ? "+" : ""}{numberID(d)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        <div><Label className="text-xs text-slate-500">Alasan Koreksi (wajib)</Label><Textarea rows={2} className="mt-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Jelaskan alasan koreksi (mis. salah input operator)..." data-testid="prod-correction-reason" /></div>
        <p className="text-[11px] text-slate-400">Invarian wajib: Cutting ≤ Target, Sewing ≤ Cutting, Finishing ≤ Sewing, dan Antrian QC + QC Passed + Reject Menunggu + Rework + Scrap = Finishing.</p>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button disabled={busy || changes.length === 0} onClick={submit} data-testid="prod-correction-submit" className="bg-indigo-600 hover:bg-indigo-500">{busy ? "..." : "Simpan Koreksi"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CorrectionHistory({ pid }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/production/production-orders/${pid}/corrections`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [pid]);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="rounded-xl border border-slate-200" data-testid="prod-correction-history">
      <div className="border-b border-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">Riwayat Koreksi (Owner)</div>
      <div className="divide-y divide-slate-100">
        {rows.map((c) => (
          <div key={c.id} className="px-4 py-2 text-xs" data-testid={`prod-correction-hist-${c.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-1">
              <span className="font-mono text-slate-600">{c.sku} · {c.size}</span>
              <span className="text-slate-400">{c.owner_name} · {fmtWib(c.created_at)}{c.status && c.status !== "APPLIED" ? ` · ${c.status}` : ""}</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(c.changes || []).map((ch, i) => (
                <span key={i} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">{ch.field_label}: {numberID(ch.old_value)} → {numberID(ch.new_value)} <span className={ch.delta > 0 ? "text-emerald-600" : "text-rose-600"}>({ch.delta > 0 ? "+" : ""}{numberID(ch.delta)})</span></span>
              ))}
            </div>
            <div className="mt-1 text-slate-500">Alasan: {c.reason}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailDialog({ id, onClose, onChanged, onOpenPo }) {
  const { can } = useAuth();
  const [po, setPo] = useState(null);
  const [stage, setStage] = useState(null);
  const [inputs, setInputs] = useState({});
  const [busy, setBusy] = useState(false);

  const reload = () => api.get(`/production/production-orders/${id}`).then((r) => setPo(r.data));
  useEffect(() => { reload(); }, [id]);

  const refreshAll = () => { reload(); onChanged(); };
  const [editing, setEditing] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [replOpen, setReplOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [recon, setRecon] = useState(null);
  useEffect(() => {
    if (po && po.status === "Dibatalkan") {
      api.get(`/production/production-orders/${id}/reconciliation`).then((r) => { setRecon(r.data); setPo((p) => ({ ...p, has_downstream: r.data.has_downstream })); }).catch(() => { setPo((p) => ({ ...p, has_downstream: true })); });
    }
  }, [po?.id, po?.status]);
  const act = async (path, ok) => { setBusy(true); try { await api.post(`/production/production-orders/${id}/${path}`); toast.success(ok); refreshAll(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const doRecreate = async () => { setBusy(true); try { const r = await api.post(`/production/production-orders/${id}/recreate`); toast.success(`PO baru dibuat: ${r.data.po_prod_no} (Draft)`); refreshAll(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };

  const availStages = STAGES.filter((s) => can(s.perm));
  const submitStage = async () => {
    const s = STAGES.find((x) => x.key === stage);
    const items = Object.entries(inputs).map(([item_id, v]) => {
      const row = { item_id };
      s.fields.forEach(([fk]) => { row[fk] = Number(v[fk] || 0); });
      return row;
    }).filter((row) => s.fields.some(([fk]) => row[fk] > 0));
    if (items.length === 0) return toast.error("Isi qty minimal 1 item");
    setBusy(true);
    try {
      await api.post(`/production/production-orders/${id}/${stage}`, { items });
      toast.success(`${s.label} tersimpan`); setStage(null); setInputs({}); refreshAll();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!po) return <Dialog open onOpenChange={onClose}><DialogContent className="max-w-5xl"><DialogHeader><DialogTitle className="font-display">Memuat Production Order</DialogTitle></DialogHeader><Loading /></DialogContent></Dialog>;

  const canManage = can("production.order.manage");
  const canApprove = can("production.order.approve");
  const active = ["Disetujui", "Proses Produksi"].includes(po.status);
  const s = stage ? STAGES.find((x) => x.key === stage) : null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="prod-detail-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-3">{po.po_prod_no} <StatusBadge status={po.status} /></DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{po.fabric_name} · {po.color_name} · {po.warehouse_name}{po.priority ? ` · Prioritas ${po.priority}` : ""}{po.target_completion_date ? ` · Target ${formatDate(po.target_completion_date)}` : ""}</p>
        {po.recreated_from_po_prod_no && (
          <div data-testid="prod-detail-recreated-badge">
            <button type="button" onClick={() => onOpenPo && onOpenPo(po.recreated_from_production_order_id)} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100" title={`Lihat PO sumber ${po.recreated_from_po_prod_no}`}>↻ {po.is_replacement ? "Pengganti" : "Dibuat ulang"} dari {po.recreated_from_po_prod_no}</button>
          </div>
        )}

        <p className="text-xs text-slate-500" data-testid="prod-detail-creator">Dibuat Oleh: <span className="font-semibold text-slate-700">{po.created_by_name || "Tidak tersedia"}</span>{po.created_at && <span className="text-slate-400"> · Dibuat {fmtWib(po.created_at)}</span>}</p>

        {po.fabric_plan && (
          <div data-testid="prod-fabric-plan" className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm">
            {po.fabric_plan.material_source_mode === "CARRY_FORWARD" && (
              <div className="mb-1" data-testid="prod-source-carry-badge">
                <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700">↩ Sumber Material: Lanjutkan Material/WIP dari {po.fabric_plan.replacement_of_po_prod_no || po.recreated_from_po_prod_no}</span>
              </div>
            )}
            <span className="font-semibold text-slate-700">Kebutuhan Kain (Roll): </span>
            {po.fabric_plan.planned_fabric_roll_qty == null
              ? (po.fabric_plan.material_source_mode === "CARRY_FORWARD"
                  ? <span className="text-slate-500" data-testid="prod-fabric-plan-carry-opt">tidak wajib (material/WIP dilanjutkan). Tambahkan hanya jika terjadi kekurangan.</span>
                  : <span className="text-slate-400">belum ditetapkan</span>)
              : <span data-testid="prod-fabric-plan-summary">Rencana <b>{numberID(po.fabric_plan.planned_fabric_roll_qty)}</b> · Sudah Dialokasikan <b>{numberID(po.fabric_plan.effective_roll_usage)}</b> · Sisa <b>{numberID(po.fabric_plan.remaining_roll_qty)}</b> Roll{po.fabric_plan.fulfilled && <span className="ml-1 font-semibold text-emerald-600">— Kebutuhan terpenuhi</span>}</span>}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {canManage && po.status === "Draft" && <Button size="sm" disabled={busy} onClick={() => act("submit", "Diajukan untuk persetujuan")} data-testid="prod-submit-btn" className="bg-indigo-600 hover:bg-indigo-500">Ajukan</Button>}
          {canManage && ["Draft", "Disetujui"].includes(po.status) && <Button size="sm" variant="outline" disabled={busy} onClick={() => setEditing(true)} data-testid="prod-edit-btn">Edit</Button>}
          {canManage && !["Selesai", "Dibatalkan", "Ditolak"].includes(po.status) && <Button size="sm" variant="outline" disabled={busy} onClick={() => setPlanOpen(true)} data-testid="prod-fabric-plan-btn" className="text-indigo-600 border-indigo-200">{po.fabric_plan?.planned_fabric_roll_qty == null ? (po.fabric_plan?.material_source_mode === "CARRY_FORWARD" ? "Tambah Kebutuhan Kain (opsional)" : "Tetapkan Kebutuhan Kain") : "Tambah Kebutuhan Kain"}</Button>}
          <Button size="sm" variant="outline" onClick={() => downloadPdf(`/pdf/production-order/${po.id}`)} data-testid="prod-pdf-btn">Cetak / Ekspor PDF</Button>
          <Button size="sm" variant="outline" onClick={() => downloadPdf(`/pdf/qc-completion/${po.id}`)} data-testid="prod-qc-pdf-btn">Cetak QC / Penyelesaian</Button>
          {canApprove && po.status === "Menunggu Persetujuan" && <><Button size="sm" disabled={busy} onClick={() => act("approve", "PO disetujui")} data-testid="prod-approve-btn" className="bg-emerald-600 hover:bg-emerald-500">Setujui</Button><Button size="sm" variant="outline" disabled={busy} onClick={() => act("reject", "PO ditolak")} data-testid="prod-reject-btn" className="text-rose-600">Tolak</Button></>}
          {canManage && po.status === "Proses Produksi" && <Button size="sm" variant="outline" disabled={busy} onClick={() => act("complete", "PO diselesaikan")} data-testid="prod-complete-btn">Selesaikan</Button>}
          {can("production.order.correct") && po.status === "Proses Produksi" && <Button size="sm" variant="outline" disabled={busy} onClick={() => setCorrectOpen(true)} data-testid="prod-correct-btn" className="text-amber-700 border-amber-300">Koreksi Produksi</Button>}
          {canManage && po.status === "Dibatalkan" && !po.has_downstream && <Button size="sm" variant="outline" disabled={busy} onClick={doRecreate} data-testid="prod-recreate-btn" className="text-indigo-600 border-indigo-200">Buat Ulang / Duplikat</Button>}
          {canManage && po.status === "Dibatalkan" && po.has_downstream && <Button size="sm" variant="outline" disabled={busy} onClick={() => setReplOpen(true)} data-testid="prod-replacement-btn" className="text-amber-700 border-amber-300">Buat PO Pengganti</Button>}
          {canManage && !["Selesai", "Dibatalkan", "Ditolak"].includes(po.status) && <Button size="sm" variant="ghost" disabled={busy} onClick={() => setCancelOpen(true)} className="text-rose-600" data-testid="prod-cancel-btn">Batalkan</Button>}
        </div>
        {editing && <CreateDialog editPo={po} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refreshAll(); }} />}
        {cancelOpen && <CancelDialog po={po} onClose={() => setCancelOpen(false)} onDone={() => { setCancelOpen(false); refreshAll(); }} />}
        {replOpen && <ReplacementDialog po={po} recon={recon} onClose={() => setReplOpen(false)} onDone={() => { setReplOpen(false); refreshAll(); }} />}
        {planOpen && <FabricPlanDialog po={po} onClose={() => setPlanOpen(false)} onDone={() => { setPlanOpen(false); refreshAll(); }} />}
        {correctOpen && <CorrectionDialog po={po} onClose={() => setCorrectOpen(false)} onDone={() => { setCorrectOpen(false); refreshAll(); }} />}

        <div className="rounded-xl border border-slate-200 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>SKU / Ukuran</TableHead><TableHead className="text-right">Target</TableHead><TableHead className="text-right">Cut</TableHead><TableHead className="text-right">Sewn</TableHead><TableHead className="text-right">Finish</TableHead><TableHead className="text-right">Antrian QC</TableHead><TableHead className="text-right">Passed</TableHead><TableHead className="text-right">Reject Menunggu</TableHead><TableHead className="text-right">Rework</TableHead><TableHead className="text-right">Scrap</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {po.items.map((it) => (
                <TableRow key={it.id} data-testid={`prod-item-${it.sku}`}>
                  <TableCell><div className="font-mono text-xs">{it.sku}</div><div className="text-xs text-slate-400">{it.age_name} · {it.sleeve_name} · {it.size_name}</div></TableCell>
                  <TableCell className="text-right font-medium">{numberID(it.target_qty)}</TableCell>
                  <TableCell className="text-right">{numberID(it.cut_qty)}</TableCell>
                  <TableCell className="text-right">{numberID(it.sewn_qty)}</TableCell>
                  <TableCell className="text-right">{numberID(it.finished_qty)}</TableCell>
                  <TableCell className="text-right text-amber-600">{numberID(it.qc_queue)}</TableCell>
                  <TableCell className="text-right text-emerald-600 font-medium">{numberID(it.qc_passed_qty)}</TableCell>
                  <TableCell className="text-right text-rose-600">{numberID(it.qc_failed_pending)}</TableCell>
                  <TableCell className="text-right">{numberID(it.rework_qty)}</TableCell>
                  <TableCell className="text-right text-slate-400">{numberID(it.scrap_qty)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {active && availStages.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-3 flex flex-wrap gap-2">
              {availStages.map((st) => (
                <Button key={st.key} size="sm" variant={stage === st.key ? "default" : "outline"} onClick={() => { setStage(st.key); setInputs({}); }} data-testid={`prod-stage-${st.key}`} className={stage === st.key ? "bg-indigo-600 hover:bg-indigo-500" : ""}>{st.label}</Button>
              ))}
            </div>
            {s && (
              <div>
                <p className="mb-2 text-xs font-semibold text-slate-500">Input {s.label} per ukuran</p>
                <div className="space-y-2">
                  {po.items.map((it) => (
                    <div key={it.id} className="grid grid-cols-12 items-center gap-2">
                      <span className="col-span-4 font-mono text-xs">{it.sku} <span className="text-slate-400">({it.age_name} {it.sleeve_name} {it.size_name})</span></span>
                      {s.fields.map(([fk, flabel]) => (
                        <div key={fk} className="col-span-3">
                          <Input type="number" min="0" placeholder={flabel} value={inputs[it.id]?.[fk] || ""} onChange={(e) => setInputs({ ...inputs, [it.id]: { ...inputs[it.id], [fk]: e.target.value } })} data-testid={`stage-${s.key}-${it.sku}-${fk}`} />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex justify-end"><Button size="sm" disabled={busy} onClick={submitStage} data-testid="stage-submit-btn" className="bg-indigo-600 hover:bg-indigo-500">Simpan {s.label}</Button></div>
              </div>
            )}
          </div>
        )}

        <CorrectionHistory pid={id} />

        <DialogFooter><Button variant="outline" onClick={onClose}>Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
